import { Wallet, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { usePayoutBalance } from '../hooks/useDashboard';

// Low-balance thresholds (INR). Not from a spec — tune as needed.
const LOW_BALANCE_THRESHOLD = 5000;
const CRITICAL_BALANCE_THRESHOLD = 1000;

function formatBalance(amount: number): string {
  return `₹${amount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function PayoutBalanceCard() {
  const { data, isLoading } = usePayoutBalance();

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div className="h-4 w-24 bg-muted animate-pulse rounded" />
          <div className="h-8 w-8 bg-muted animate-pulse rounded-full" />
        </div>
        <div className="mt-3 h-8 w-32 bg-muted animate-pulse rounded" />
        <div className="mt-2 h-4 w-20 bg-muted animate-pulse rounded" />
      </Card>
    );
  }

  // Payouts not configured on the server at all — not an error state.
  if (!data?.configured) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-muted-foreground">Payouts Balance</p>
          <div className="rounded-full p-2 bg-muted text-muted-foreground">
            <Wallet className="h-4 w-4" />
          </div>
        </div>
        <div className="mt-3">
          <h3 className="text-lg font-semibold text-muted-foreground">Not configured</h3>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Cashfree Payouts isn't set up on this server.
        </p>
      </Card>
    );
  }

  const balance = data.available_balance;
  const isCritical = balance !== null && balance < CRITICAL_BALANCE_THRESHOLD;
  const isLow = balance !== null && !isCritical && balance < LOW_BALANCE_THRESHOLD;

  const iconWrapClass = isCritical
    ? 'bg-destructive/10 text-destructive'
    : isLow
      ? 'bg-warning/10 text-warning'
      : 'bg-primary/10 text-primary';

  return (
    <Card
      className={cn(
        'p-6',
        isCritical && 'border-destructive/40 bg-destructive/5',
        isLow && 'border-warning/40 bg-warning/5',
      )}
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">Payouts Balance</p>
        <div className={cn('rounded-full p-2', iconWrapClass)}>
          <Wallet className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-3">
        {balance !== null ? (
          <h3 className="text-2xl font-bold">{formatBalance(balance)}</h3>
        ) : (
          <h3 className="text-lg font-semibold text-muted-foreground">Unavailable</h3>
        )}
      </div>
      {balance !== null ? (
        (isCritical || isLow) && (
          <div className={cn('mt-2 flex items-center gap-1 text-sm', isCritical ? 'text-destructive' : 'text-warning')}>
            <AlertTriangle className="h-3.5 w-3.5" />
            <span className="font-medium">
              {isCritical ? 'Critically low balance' : 'Low balance'}
            </span>
          </div>
        )
      ) : (
        <p
          className="mt-2 text-sm text-muted-foreground truncate"
          title={data.error || 'Balance unavailable from Cashfree'}
        >
          {data.error || 'Balance unavailable from Cashfree'}
        </p>
      )}
    </Card>
  );
}
